import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@edusense/shared-auth-core';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  axisLabel,
  isLowInformationReviewer,
  notableAxes,
  targetTypeLabel,
  type ReviewCandidate,
  type UserReviewProfile,
} from '../types/reviews';

export default function ReviewAdminPage() {
  const { token: accessToken } = useAuth();
  const [candidates, setCandidates] = useState<ReviewCandidate[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<ReviewCandidate | null>(null);
  const [profile, setProfile] = useState<UserReviewProfile | null>(null);
  const [publishing, setPublishing] = useState(false);

  const getAuthHeaders = useCallback(() => {
    const headers: Record<string, string> = accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
    const xsrfCookie = document.cookie.split('; ').find(row => row.startsWith('XSRF-TOKEN='));
    if (xsrfCookie) {
      headers['X-XSRF-TOKEN'] = xsrfCookie.split('=')[1];
    }
    return headers;
  }, [accessToken]);

  const fetchCandidates = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get<{ candidates: ReviewCandidate[] }>(
        '/api/admin/reviews/candidates',
        { headers: getAuthHeaders(), withCredentials: true },
      );
      setCandidates(response.data.candidates ?? []);
    } catch (error) {
      console.error('Failed to fetch review candidates:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    void fetchCandidates();
  }, [fetchCandidates]);

  const openCandidate = async (candidate: ReviewCandidate) => {
    setSelected(candidate);
    setProfile(null);
    try {
      const response = await axios.get<UserReviewProfile>(
        `/api/admin/reviews/users/${encodeURIComponent(candidate.review.user_id)}`,
        { headers: getAuthHeaders(), withCredentials: true },
      );
      setProfile(response.data);
    } catch (error) {
      console.error('Failed to fetch reviewer profile:', error);
    }
  };

  const publish = async (reviewId: string, curated: boolean) => {
    setPublishing(true);
    try {
      await axios.put(
        `/api/admin/reviews/${reviewId}/curate`,
        { curated },
        { headers: getAuthHeaders(), withCredentials: true },
      );
      setSelected(null);
      setProfile(null);
      void fetchCandidates();
    } catch (error) {
      console.error('Failed to update curation:', error);
      alert('공개 상태 변경에 실패했습니다.');
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div className="space-y-6 p-6">
      <header className="space-y-1">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">해석 리뷰 관리</h1>
          {/* The admin app has no sidebar, so the two halves of this loop link to each other. */}
          <a href="/reviews/draw" className="text-sm font-medium text-primary hover:underline">
            기록 추첨 →
          </a>
        </div>
        <p className="text-sm text-muted-foreground">
          공개에 동의한 리뷰만 목록에 오릅니다. 동의는 후보 자격일 뿐이고, 공개는 여기서 선별합니다.
        </p>
      </header>

      <div className="grid gap-6 lg:grid-cols-2">
        <section className="space-y-2">
          <h2 className="text-lg font-semibold">
            전체 리뷰 <Badge variant="secondary">{candidates.length}</Badge>
          </h2>

          {loading ? (
            <p className="text-sm text-muted-foreground">불러오는 중…</p>
          ) : candidates.length === 0 ? (
            <p className="text-sm text-muted-foreground">아직 리뷰가 없습니다.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>대상</TableHead>
                  <TableHead>축별 점수</TableHead>
                  <TableHead>작성</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {candidates.map((candidate) => (
                  <TableRow key={candidate.review.id}>
                    <TableCell>
                      <div className="font-medium">{targetTypeLabel(candidate.review.target_type)}</div>
                      <div className="text-xs text-muted-foreground">{candidate.review.target_key}</div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {Object.entries(candidate.review.axis_scores).map(([axis, score]) => (
                          <Badge key={axis} variant="outline">
                            {axisLabel(axis)} {score}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      <div>{candidate.review.created_at?.slice(0, 10) ?? '-'}</div>
                      {candidate.review.is_public_consented
                        ? <Badge variant="secondary" className="mt-1">공개 동의</Badge>
                        : <span className="text-[11px] text-muted-foreground">비공개</span>}
                    </TableCell>
                    <TableCell>
                      <Button variant="outline" size="sm" onClick={() => void openCandidate(candidate)}>
                        원문 대조
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </section>

        <section className="space-y-4">
          <h2 className="text-lg font-semibold">원문 대조</h2>

          {!selected ? (
            <p className="text-sm text-muted-foreground">
              리뷰를 선택하면 그 리뷰가 가리키는 해석 원문을 나란히 봅니다.
            </p>
          ) : (
            <div className="space-y-4">
              {/*
                The snapshot is the point of this screen: "정확했어요" means nothing without the
                text it was said about, and prompts change often enough that the original cannot
                be regenerated later.
              */}
              <div className="rounded-md border p-3">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-sm font-semibold">해석 원문</span>
                  {selected.snapshot && (
                    <span className="text-xs text-muted-foreground">
                      {selected.snapshot.model_name ?? '모델 미기록'} / {selected.snapshot.prompt_version ?? '버전 미기록'}
                    </span>
                  )}
                </div>
                {selected.snapshot ? (
                  <>
                    <div className="mb-2 flex flex-wrap gap-1">
                      {selected.snapshot.axes.map((axis) => (
                        <Badge key={axis} variant="secondary">{axisLabel(axis)}</Badge>
                      ))}
                    </div>
                    <p className="whitespace-pre-wrap text-sm">{selected.snapshot.body}</p>
                  </>
                ) : (
                  <p className="text-sm text-destructive">
                    원문 스냅샷이 없습니다. 대조 없이 공개하지 마세요.
                  </p>
                )}
              </div>

              <div className="rounded-md border p-3">
                <div className="mb-2 text-sm font-semibold">리뷰</div>
                <div className="mb-2 flex flex-wrap gap-1">
                  {Object.entries(selected.review.axis_scores).map(([axis, score]) => (
                    <Badge key={axis} variant="outline">{axisLabel(axis)} {score}</Badge>
                  ))}
                </div>
                <p className="whitespace-pre-wrap text-sm">{selected.review.comment ?? '(주관식 없음)'}</p>
              </div>

              {profile && (
                <div className="rounded-md border p-3">
                  <div className="mb-2 text-sm font-semibold">작성자 프로필</div>
                  <div className="mb-2 flex flex-wrap gap-2 text-xs">
                    <span>적중 체감도 {profile.satisfaction_score ?? '-'}</span>
                    <span>리뷰어 가중치 {profile.reviewer_weight}</span>
                    {isLowInformationReviewer(profile) && (
                      <Badge variant="destructive">정보량 낮음</Badge>
                    )}
                  </div>

                  {/*
                    Deviation, not the average. An average blends how well readings landed with
                    how generously this person rates, so 4.2 from a lenient reviewer and 4.2 from
                    a strict one look identical while meaning opposite things.
                  */}
                  {notableAxes(profile.axis_breakdown).length === 0 ? (
                    <p className="text-xs text-muted-foreground">
                      축별로 두드러진 차이가 없습니다 (표본 부족이거나 전 항목 비슷한 점수).
                    </p>
                  ) : (
                    <ul className="space-y-1 text-xs">
                      {notableAxes(profile.axis_breakdown).map((row) => (
                        <li key={row.axis} className="flex items-center gap-2">
                          <span className="w-16">{axisLabel(row.axis)}</span>
                          <span className={row.deviation >= 0 ? 'text-emerald-600' : 'text-rose-600'}>
                            {row.deviation >= 0 ? '+' : ''}{row.deviation}
                          </span>
                          <span className="text-muted-foreground">
                            (평균 {row.mean} / 본인 전체 {row.user_overall_mean} / n={row.n})
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              <div className="flex gap-2">
                {/*
                  Publishing is refused server-side without consent; disabling here makes that
                  visible instead of surfacing it as an error after the click.
                */}
                <Button
                  onClick={() => void publish(selected.review.id, true)}
                  disabled={publishing || !selected.snapshot || !selected.review.is_public_consented}
                >
                  {selected.review.is_public_consented ? '공개하기' : '공개 불가 (미동의)'}
                </Button>
                <Button variant="outline" onClick={() => { setSelected(null); setProfile(null); }}>
                  닫기
                </Button>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
